import React, { use, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaRegTrashAlt } from "react-icons/fa";
import { useCurrency } from "../utils/currency";

// Same palette the checkout page uses, so the two pages read as one flow.
const GOLD = "#DFBD69";
const GOLD_DIM = "rgba(223,189,105,0.7)";
const CARD_BG = "#0A031E";

const styles = {
  container: {
    // Fill at least a screen even when the cart is short, so the site footer
    // starts below the fold and the user scrolls to it rather than landing on
    // it. The layout only renders the footer here on desktop.
    minHeight: "100vh",
    // overflowY: "auto",
    // backgroundColor: "#171520",
    padding: "16px",
    position: "relative",
    fontFamily: "'DMSans', Arial, sans-serif", // use Shop body font
    color: "#fff2d2",
  },
  wrapper: {
    maxWidth: "1000px",
    margin: "0 auto",
  },
  heading: {
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "24px",
    color: "white",
    fontFamily: "'PlayfairDisplay', serif",
    fontWeight: 800,
  },
  emptyText: {
    color: "#DFBD69",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  card: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#0A031E", // match Shop product card background
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    alignItems: "center",
    // border: "2px solid #DFBD69", // Gold border
    border: "2px solid rgba(201,168,75,0.4)",
  },

  image: {
    width: "96px",
    height: "96px",
    objectFit: "cover",
    borderRadius: "8px",
    backgroundColor: "#0A031E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    color: "#6b7280",
    willChange: "transform", // Add this to force GPU acceleration
    backfaceVisibility: "hidden", // Add this to prevent rendering issues
  },
  details: {
    flex: 1,
  },
  quantityControls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
  },
  qtyButton: {
    padding: "4px 8px",
    borderRadius: "4px",
    border: "1px solid #d1d5db",
    backgroundColor: "#f3f4f6",
    cursor: "pointer",
    width: "32px", // 👈 add this line for equal width
  },
  removeBtn: {
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  buyNowBtn: {
    marginTop: "24px",
    marginBottom: "12px",
    alignSelf: "center",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: "#10b981",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
  },
  modal: {
    backgroundColor: "black",
    padding: "24px",
    borderRadius: "12px",
    maxWidth: "400px",
    width: "100%",
    maxHeight: "80vh", // allow modal to scroll if content is long
    overflowY: "auto",
    zIndex: 10002, // above overlay
  },
  confirmBtn: {
    marginTop: "16px",
    padding: "10px",
    backgroundColor: "#debb00",
    color: "#151517",
    border: "none",
    borderRadius: "8px",
    width: "100%",
    fontWeight: "bold",
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "space-between",
    height: "100%",
  },

  /* --- Desktop two-column shell, mirroring the checkout page --- */
  desktopGrid: {
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
  },
  leftColumn: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  // Follows the page while the item list scrolls past, then scrolls away with
  // the rest of the page once its own content ends — so it never covers the
  // site footer. Same behaviour as the checkout sidebar.
  rightColumn: {
    flex: "0 0 340px",
    position: "sticky",
    top: "16px",
  },
  panel: {
    backgroundColor: CARD_BG,
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid rgba(223,189,105,0.2)",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "1px solid rgba(223,189,105,0.15)",
  },
  itemName: {
    color: GOLD,
    fontWeight: 600,
    fontSize: "15px",
    margin: 0,
  },
  itemMeta: {
    color: GOLD_DIM,
    fontSize: "13px",
    margin: "4px 0 0",
  },
  qtyStepper: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "1px solid rgba(223,189,105,0.35)",
    borderRadius: "8px",
    padding: "4px 8px",
  },
  qtyStepBtn: {
    width: "26px",
    height: "26px",
    borderRadius: "4px",
    border: "none",
    background: "transparent",
    color: GOLD,
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // Matches the height of the quantity stepper sitting next to it.
  iconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "1px solid rgba(223,189,105,0.35)",
    borderRadius: "8px",
    cursor: "pointer",
    color: GOLD,
    fontSize: "16px",
    width: "36px",
    height: "36px",
    lineHeight: 1,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: GOLD_DIM,
    fontSize: "14px",
  },
  // Mobile only: pinned above the fold while the items scroll, and scrolls
  // away with the page instead of sitting on top of the site footer.
  footerBar: {
    position: "sticky",
    bottom: 0,
    background: "#120A26",
    borderTop: "1px solid rgba(223,189,105,0.25)",
    padding: "12px 16px",
    margin: "0 -16px -16px",
    zIndex: 50,
  },
  footerInner: {
    maxWidth: "760px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  checkoutBtn: {
    width: "100%",
    padding: "13px",
    backgroundColor: GOLD,
    color: "#151517",
    border: "none",
    borderRadius: "28px",
    fontWeight: "bold",
    fontSize: "15px",
  },
};

const getProductImageUrl = (product) => {
  // Prefer API ImageUrl, then legacy IMAGE, then ImageProdId/ImageFile, then FILENAME path
  if (!product) return null;
  if (product.ImageUrl) return product.ImageUrl;
  if (product.IMAGE) return product.IMAGE;
  if (product.ImageProdId && product.ImageFile) {
    return `${process.env.PUBLIC_URL}/static/media/product/${product.ImageProdId.toString().padStart(
      5,
      "0",
    )}/${product.ImageFile}`;
  }
  if (product.ID && product.FILENAME) {
    return `${process.env.PUBLIC_URL}/static/media/product/${product.ID.toString().padStart(
      5,
      "0",
    )}/${product.FILENAME}.png`;
  }
  return null;
};

const Cart = () => {
  const { t } = useTranslation();
  const { convert, format } = useCurrency();
  const [cart, setCart] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const userID = localStorage.getItem("userID");
  const [currentCoins, setCurrentCoins] = useState();
  const token = "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp";
  const [uid, setUid] = useState("");
  const [DC1, setDC1] = useState(0);
  const [DC2, setDC2] = useState(0);
  const [DC3, setDC3] = useState(0);
  const [DC4, setDC4] = useState(0);
  const [DC5, setDC5] = useState(0);
  const [DC6, setDC6] = useState(0);
  const [DC7, setDC7] = useState(0);
  const [DC8, setDC8] = useState(0);
  const [DC9, setDC9] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [guardianModal, setGuardianModal] = useState(false);
  const userAgeRef = useRef(undefined); // undefined = not yet fetched, null = fetched / no data

  // Same breakpoint the checkout page and Tryoutpage use, so every page in the
  // flow switches between its mobile and desktop layout at the same width.
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 852);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 852);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchUserAge = async () => {
    // test
    // return 15; // TEMP: force under-18 to test guardian modal — remove before commit
    if (userAgeRef.current !== undefined) return userAgeRef.current;
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserName: localStorage.getItem("username") || "",
            ID: parseInt(userID || "0", 10),
            LangId:
              parseInt(localStorage.getItem("languageId") || "2224", 10) ||
              2224,
            UserToken: "",
            Token: {
              AccessToken:
                "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
            },
          }),
        },
      );
      if (!res.ok) {
        userAgeRef.current = null;
        return null;
      }
      const data = await res.json();
      const age = data?.Profiles?.[0]?.Age ?? null;
      userAgeRef.current = age;
      return age;
    } catch {
      userAgeRef.current = null;
      return null;
    }
  };

  // Exchange rate: 1$ equals 10 Gems
  const EXCHANGE_RATE = 10;
  const CARD_TO_GEMS = (amt) => (Number(amt) * EXCHANGE_RATE).toFixed(2);

  const navigate = useNavigate();
  const location = useLocation();

  // Checkout pushes /cart onto the history stack, so a plain navigate(-1) here
  // would bounce the user straight back into checkout. When that's where they
  // came from (or there's nothing behind us), send them to the shop instead.
  const handleBack = () => {
    if (location.state?.from === "checkout" || window.history.state?.idx === 0) {
      navigate("/shop");
    } else {
      navigate("/shop");
    }
  };
  const fetchTotalCoins = async () => {
    try {
      // Make the GET request
      const response = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/booking/getTotal?ID=${userID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Check if the response is OK
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      // Parse the JSON response
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        console.warn("Invalid data format from API:", data);
        setCurrentCoins(0);
        return 0;
      }
      const coins = data[0].COINS;
      localStorage.setItem("coins", coins);
      setCurrentCoins(coins);
      return coins;
    } catch (error) {
      console.error("Error fetching data:", error);
      return 0;
    }
  };

  // Print / export receipt in landscape layout
  const printReceipt = () => {
    const purchaseDate = new Date().toLocaleString();
    // use component EXCHANGE_RATE and CARD_TO_GEMS
    const rowsHtml = cartItems
      .map((item) => {
        const name = item.DESC_OTH || item.Name || "Unnamed Product";
        const qty = item.quantity || item.Quantity || 1;
        const amount = Number(item.CardAmount ?? item.price ?? 0) || 0;
        const total = (amount * qty).toFixed(2);
        const amountStr = amount === 0 ? "0.00" : amount.toFixed(2);
        return `
          <tr>
            <td style="padding:12px;border-bottom:1px solid #e5e7eb;">${name}</td>
            <td style="padding:12px;border-bottom:1px solid #e5e7eb;text-align:center">${qty.toFixed ? qty.toFixed(2) : qty}</td>
            <td style="padding:12px;border-bottom:1px solid #e5e7eb;text-align:right">${amountStr}</td>
            <td style="padding:12px;border-bottom:1px solid #e5e7eb;text-align:right">${total} 💎</td>
          </tr>
        `;
      })
      .join("\n");

    const totalCard = cartItems
      .reduce(
        (sum, item) =>
          sum +
          Number(item.CardAmount || 0) * (item.quantity || item.Quantity || 1),
        0,
      )
      .toFixed(2);
    const totalCardGems = (Number(totalCard) * EXCHANGE_RATE).toFixed(2);

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Receipt</title>
        <style>
          @page { size: landscape; margin: 20mm; }
          body { font-family: Arial, Helvetica, sans-serif; color: #222; }
          .receipt { width: 100%; max-width: 1100px; margin: 0 auto; }
          h1 { color: #0f5132; font-size: 20px; }
          .meta { margin-bottom: 12px; color: #333; }
          table { width: 100%; border-collapse: collapse; background: #fff; }
          th { background:#f3f4f6; text-align:left; padding:12px; border-bottom:2px solid #e5e7eb; }
          td { padding:12px; vertical-align: middle; }
          .right { text-align:right; }
          .total-row td { font-weight:700; border-top:2px solid #d1d5db; }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div style="display:flex;align-items:center;margin-bottom:8px;">
            <div style="flex:1"><h1>Purchase Date  :  ${purchaseDate}</h1></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Charm</th>
                <th style="width:120px;text-align:center">Qty</th>
                <th style="width:150px;text-align:right">Amount</th>
                <th style="width:150px;text-align:right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr class="total-row">
                <td></td>
                <td></td>
                <td class="right">Total</td>
                <td class="right">${Number(totalCard).toFixed(2)} 💎</td>
              </tr>
              <tr>
                <td colspan="3" style="padding:12px;text-align:right;font-weight:700">Exchange Rate</td>
                <td style="padding:12px;text-align:right;color:#0f5132">1$ = ${EXCHANGE_RATE} Gems</td>
              </tr>
              <tr>
                <td colspan="3" style="padding:12px;text-align:right;font-weight:700">Card Total in Gems</td>
                <td style="padding:12px;text-align:right;color:#0f5132">${totalCardGems} Gems</td>
              </tr>
            </tbody>
          </table>
        </div>
        <script>
          setTimeout(() => { window.print(); }, 300);
        </script>
      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (!w) {
      alert("Popup blocked. Please allow popups to print the receipt.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // const fetchDigitalCharm = async () => {
  //   try {
  //     const response = await fetch(
  //       `https://xianhefairy.com/LotusFairy_API/api/booking/getDigitalCharm?ID=${userID}`
  //     );
  //     const data = await response.json();

  //     if (!Array.isArray(data) || data.length === 0) {
  //       setUid("");
  //       return;
  //     }

  //     const charmData = data[0];
  //     setUid(charmData.UID);
  //     setDC1((prev) => Math.max(prev, charmData.DC_1));
  //     setDC2((prev) => Math.max(prev, charmData.DC_2));
  //     setDC3((prev) => Math.max(prev, charmData.DC_3));
  //     setDC4((prev) => Math.max(prev, charmData.DC_4));
  //     setDC5((prev) => Math.max(prev, charmData.DC_5));
  //     setDC6((prev) => Math.max(prev, charmData.DC_6));
  //     setDC7((prev) => Math.max(prev, charmData.DC_7));
  //     setDC8((prev) => Math.max(prev, charmData.DC_8));
  //     setDC9((prev) => Math.max(prev, charmData.DC_9));
  //   } catch (error) {
  //     console.error("Error fetching Digital Charm data:", error);
  //   }
  // };

  // const updateDCFromCart = (cartItems) => {
  //   cartItems.forEach((item) => {
  //     const id = item.ID || item.id;
  //     if (id >= 23 && id <= 30) {
  //       const index = id - 23 + 1;
  //       const setter = {
  //         1: setDC1,
  //         2: setDC2,
  //         3: setDC3,
  //         4: setDC4,
  //         5: setDC5,
  //         6: setDC6,
  //         7: setDC7,
  //         8: setDC8,
  //       }[index];
  //       if (setter) setter((prev) => Math.max(prev, 1));
  //     }
  //   });
  // };

  const fetchCart = async () => {
    try {
      const [cartRes, productRes, drawsRes] = await Promise.all([
        fetch(
          `https://xianhefairy.com/LotusFairy_API/api/Usercart/GetUserCart?CustId=${userID}`,
        ),
        fetch(`https://xianhefairy.com/LotusFairy_API/api/member/getProduct`),
        fetch(`https://xianhefairy.com/LotusFairy_API/api/draws/GetListDraws`),
      ]);
      if (!cartRes.ok) throw new Error("Failed to fetch cart from server");

      const cartApiResult = await cartRes.json();
      console.log("Cart format:", cartApiResult);
      if (!Array.isArray(cartApiResult) || cartApiResult.length === 0) {
        setCart([]);
        setLoading(false);
        return;
      }

      const productData = productRes.ok ? await productRes.json() : [];
      const drawsData = drawsRes.ok ? await drawsRes.json() : [];

      const mappedCart = cartApiResult.map((item) => {
        const matched = Array.isArray(productData)
          ? productData.find((p) => p.ID === item.ProdId)
          : null;
        const isWheel = matched
          ? (matched.CATEGORY_DESC || "").toLowerCase().includes("wheel")
          : false;
        const isPhysical = matched
          ? !isWheel &&
            ["your style", "the living edit", "jewelry"].some((cat) =>
              (matched.CATEGORY_DESC || "").toLowerCase().includes(cat),
            )
          : false;

        // For wheel items, find the original draw ID from GetListDraws by matching name
        let drawId = null;
        if (isWheel && Array.isArray(drawsData)) {
          const draw = drawsData.find(
            (d) =>
              d.Description === item.ProdDesc ||
              d.Description === matched?.DESC_ENG,
          );
          drawId = draw ? draw.Id : null;
        }

        return {
          Uid: item.Uid,
          CustId: item.CustId,
          ProdId: item.ProdId,
          ProdDesc: item.ProdDesc,
          Remark: item.ProdRemarks,
          Qty: item.Qty,
          CardAmount: item.TotalAmount,
          GemAmount: item.GemTotal,
          ProdGif:
            item.ProdGif || (isWheel && matched ? matched.ImageUrl : null),
          ProdThumbnail:
            item.ProdThumbnail ||
            (isWheel && matched ? matched.TUMB_URL || matched.ImageUrl : null),
          isWheel,
          isPhysical,
          DrawId: drawId,
        };
      });

      setCart(mappedCart);
    } catch (error) {
      console.error("Error fetching cart:", error);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
    fetchTotalCoins();
  }, []);

  // useEffect(() => {
  //   const fetchProductsAndCart = async () => {
  //     try {
  //       // Fetch the product list
  //       const productRes = await fetch(
  //         "https://xianhefairy.com/LotusFairy_API/api/member/getProduct"
  //       );
  //       const productData = await productRes.json();
  //       setProductList(productData); // Set full product list for later use

  //       // Fetch the cart
  //       const cartRes = await fetch(
  //         `https://xianhefairy.com/LotusFairy_API/api/booking/getCart?ID=${userID}`
  //       );
  //       if (!cartRes.ok) throw new Error("Failed to fetch cart from server");

  //       const cartApiResult = await cartRes.json();
  //       if (!Array.isArray(cartApiResult) || cartApiResult.length === 0) {
  //         setCart([]);
  //         return;
  //       }

  //       // Parse the CART field (which is a stringified array)
  //       const parsedCart = JSON.parse(cartApiResult[0]?.CART || "[]");

  //       // Match product details from the fetched product list and prefer API fields (DESC_ENG / CASH_AMT / ImageUrl)
  //       const matchedCart = parsedCart.map((cartItem) => {
  //         const product = productData.find((p) => p.ID === cartItem.Product_ID);
  //         return {
  //           Product_ID: cartItem.Product_ID,
  //           Quantity: cartItem.Quantity,
  //           // Use server-provided name and price fields when available
  //           Name:
  //             product?.DESC_ENG || product?.DISPLAY_NAME || product?.FILENAME || "Unknown Product",
  //           Price: product?.CASH_AMT ?? product?.COINS ?? 0,
  //           COINS: product?.CASH_AMT ?? product?.COINS ?? 0,
  //           GEM_AMT: product?.OTHER_AMT ?? 0,
  //           CATEGORY_DESC: product?.CATEGORY_DESC || "",
  //           FILENAME: product?.FILENAME || "",
  //           IMAGE: product?.ImageUrl || product?.IMAGE || product?.ImageFile || "",
  //           // keep raw API product in case more fields are needed later
  //           RAW: product || null,
  //           ID: product?.ID,
  //         };
  //       });

  //       console.log("Matched Cart:", matchedCart);
  //       setCart(matchedCart);
  //     } catch (error) {
  //       console.error("Error fetching products or cart:", error);
  //       setCart([]);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchProductsAndCart();
  //   fetchTotalCoins();
  // }, []);

  // Replace getProductImageUrl usage with direct ProdGif usage
  const getCartItemsWithDetails = () =>
    cart.map((item) => ({
      ...item,
      quantity: item.Qty,
      Name: item.ProdDesc,
      // CardAmount: item.Amount,
      // GemAmount: item.GemTotal,
      IMAGE: item.ProdGif,
      GEM_AMT: 0, // If not provided by API
      ID: item.ProdId,
    }));

  const cartItems = getCartItemsWithDetails();

  // ✅ This useEffect must be here, outside conditionals
  useEffect(() => {
    const fetchAndUpdateDC = async () => {
      // await fetchDigitalCharm();
      // updateDCFromCart(cartItems);
    };
    fetchAndUpdateDC();
  }, [cartItems]);

  // const removeFromCart = (index) => {
  //   const newCart = [...cart];
  //   newCart.splice(index, 1);
  //   updateCart(newCart); // ✅ This will dispatch the event
  // };

  // const updateCart = async (newCart) => {
  //   setCart(newCart);
  //   localStorage.setItem("cart", JSON.stringify(newCart));
  //   await persistCartToServer(newCart);

  //   // ✅ Notify header that cart was updated
  //   window.dispatchEvent(new CustomEvent('cartUpdated'));
  // };

  const updateCartQty = async (uid, qtyChange) => {
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/Usercart/UserCartUpdate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Uid: uid, Qty: qtyChange }),
        },
      );
      if (!res.ok) throw new Error("Failed to update cart quantity");
      await fetchCart();
      window.dispatchEvent(new CustomEvent("cartUpdated")); // <-- Add this line
    } catch (error) {
      console.error("Error updating cart quantity:", error);
    }
  };

  const increaseQty = async (index) => {
    const uid = cart[index].Uid;
    await updateCartQty(uid, 1);
  };

  const decreaseQty = async (index) => {
    const uid = cart[index].Uid;
    await updateCartQty(uid, -1);
  };

  const removeFromCart = async (index) => {
    const uid = cart[index].Uid;
    try {
      const res = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/Usercart/UserCartDelete?UID=${uid}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
      const result = await res.json();
      if (result.successful) {
        await fetchCart();
        window.dispatchEvent(new CustomEvent("cartUpdated")); // <-- Add this line
      } else {
        alert(result.save_msg || "Failed to remove item from cart.");
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
      alert("Error removing item from cart.");
    }
  };

  // const increaseQty = async (index) => {
  //   const newCart = [...cart];
  //   newCart[index].Quantity = (newCart[index].Quantity || 1) + 1;
  //   await updateCart(newCart); // ✅ This will dispatch the event
  // };

  // const decreaseQty = async (index) => {
  //   const newCart = [...cart];
  //   const currentQty = newCart[index].Quantity || 1;

  //   if (currentQty > 1) {
  //     newCart[index].Quantity = currentQty - 1;
  //     await updateCart(newCart); // ✅ This will dispatch the event
  //   } else {
  //     // Remove item if qty goes to zero
  //     newCart.splice(index, 1);
  //     await updateCart(newCart); // ✅ This will also dispatch the event
  //   }
  // };

  const totalCoins = cartItems.reduce(
    (sum, item) => sum + (item.COINS || item.price || 0) * (item.Quantity || 1),
    0,
  );

  const sendDigitalCharmPurchase = async () => {
    const updatedCharmData = {
      UID: uid || "",
      DC_1: DC1,
      DC_2: DC2,
      DC_3: DC3,
      DC_4: DC4,
      DC_5: DC5,
      DC_6: DC6,
      DC_7: DC7,
      DC_8: DC8,
      DC_9: DC9,
      CUST_ID: userID,
      REDEEM_CHARM: "",
      STATUS: 1,
      CREATED_BY: "AdminUser",
      CREATED_DATE: new Date().toISOString(), // optional
      UPDATED_BY: "AdminUser",
      LAST_UPDATE: new Date().toISOString(), // optional
    };

    try {
      const saveResponse = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/booking/saveDigitalCharm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedCharmData),
        },
      );

      const saveData = await saveResponse.json();
      console.log("DCS sent", updatedCharmData);
    } catch (error) {
      console.error("Error sending digital charm purchase:", error);
    }
  };

  const handleBuyNow = async () => {
    // Under-18 users need a parent/guardian to confirm before checkout continues
    const age = await fetchUserAge();
    if (age !== null && age < 18) {
      setGuardianModal(true);
      return;
    }

    await proceedBuyNow();
  };

  // Hand the order over to the checkout page, where the delivery details and
  // the payment method are chosen and the order is placed.
  const proceedBuyNow = async () => {
    if (cartItems.length === 0) return;
    navigate("/checkout", { state: { discountPercent, discountCode } });
  };

  // const handleConfirmPurchase = async () => {
  //   if (confirmLoading) return; // Prevent double click
  //   // Fetch latest gem count from API
  //   await fetchTotalCoins();

  //   // ✅ Check if user has enough gems when paying with coins (gems)
  //   if (paymentMethod === "coins") {
  //     const requiredGems = cartItems.reduce(
  //       (sum, item) => sum + (item.GEM_AMT || 0) * (item.Quantity || 1),
  //       0
  //     );

  //     if (currentCoins < requiredGems) {
  //       setShowModal(false);
  //       setInsufficientGemsModal({ show: true, required: requiredGems, current: currentCoins });
  //       return;
  //     }
  //   }

  //   try {
  //     // Build the items array for the API
  //     const items = cartItems.map((item) => ({
  //       CustomerId: Number(userID),
  //       pid: item.ID,
  //       Quantity: item.Quantity || 1,
  //       Amount: (item.COINS || item.price || 0) * (item.Quantity || 1),
  //     }));

  //     const payload = {
  //       prefix: "PCH",
  //       PAY_MODE: paymentMethod === "stripe" ? 1 : 0, // 0 = gem, 1 = credit card
  //       items,
  //     };

  //     console.log("Submitting purchase:", payload);

  //     const res = await fetch(
  //       "https://xianhefairy.com/LotusFairy_API/api/member/PostPurchaseCoin",
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!res.ok) {
  //       throw new Error("❌ Failed to submit purchase");
  //     }

  //     // ✅ After purchase, update the cart status to 1
  //     await updateCartStatus();

  //     if (paymentMethod === "stripe") {
  //       alert("🎉 Stripe payment successful!");
  //     } else {
  //       alert("🎉 Coin purchase successful!");
  //     }

  //     setCart([]);
  //     localStorage.removeItem("cart");

  //     // ✅ Notify header that cart was cleared
  //     window.dispatchEvent(new CustomEvent('cartUpdated'));

  //     // ✅ Notify Tryoutpage to update gems
  //     window.dispatchEvent(new CustomEvent('gemsUpdated'));
  //     //sendDigitalCharmPurchase();
  //   } catch (err) {
  //     console.error("Purchase failed:", err);
  //     alert("❌ Failed to complete purchase.");
  //   } finally {
  //     setShowModal(false);
  //     setConfirmLoading(false); // Reset loading
  //     //window.location.reload();
  //   }
  // };

  // // Dev / fallback dummy data population hook must be declared before any early returns
  // useEffect(() => {
  //   // Only set dummy data if cart is empty and not loading
  //   if (!loading && cart.length === 0) {
  //     setCart([
  //       {
  //         Product_ID: 1,
  //           Quantity: 2,
  //           Name: "Lotus Fairy Charm",
  //           Price: 50,
  //           COINS: 50,
  //           CATEGORY_DESC: "Charm",
  //           FILENAME: "lotus_fairy_charm",
  //           IMAGE: "",
  //           ID: 1,
  //       },
  //       {
  //         Product_ID: 2,
  //           Quantity: 1,
  //           Name: "Blessing Scroll",
  //           Price: 100,
  //           COINS: 100,
  //           CATEGORY_DESC: "Scroll",
  //           FILENAME: "blessing_scroll",
  //           IMAGE: "",
  //           ID: 2,
  //       },
  //     ]);
  //   }
  // }, [loading, cart]);

  const handleApplyDiscount = () => {
    const code = discountCode.trim().toUpperCase();
    if (code === "SAVE10") {
      setDiscountPercent(10);
      setDiscountApplied(true);
    } else if (code === "SAVE20") {
      setDiscountPercent(20);
      setDiscountApplied(true);
    } else {
      setDiscountPercent(0);
      setDiscountApplied(false);
      alert("Invalid discount code.");
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p>Loading cart...</p>
      </div>
    );
  }

  const persistCartToServer = async (updatedCartArray) => {
    try {
      // 1. Fetch the existing cart row for this user
      const cartRes = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/booking/getCart?ID=${userID}`,
      );
      if (!cartRes.ok) throw new Error("Failed to fetch cart data");
      const cartData = await cartRes.json();

      // 2. Use the first cart row (like the web does)
      const cartRow =
        Array.isArray(cartData) && cartData.length > 0 ? cartData[0] : null;
      if (!cartRow) {
        console.warn("No cart row found for user");
        return;
      }

      // 3. Build the updated payload
      const updatedPayload = {
        ...cartRow,
        CART: JSON.stringify(
          updatedCartArray.map((item) => ({
            Product_ID: item.Product_ID || item.ID,
            Quantity: item.Quantity || item.quantity || 1,
          })),
        ),
        STATUS: cartRow.STATUS, // keep the same status as existing row
      };

      // 4. POST to update the cart row
      await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/booking/AddCart?token=${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedPayload),
        },
      );
    } catch (error) {
      console.error("❌ Failed to update cart to server:", error);
    }
  };

  // In the shopper's chosen currency; each line is converted before summing so
  // the total matches what Checkout charges.
  const totalCard = cartItems.reduce(
    (sum, item) => sum + convert(item.CardAmount || 0),
    0,
  );
  const totalGems = cartItems.reduce(
    (sum, item) => sum + (item.GemAmount || 0),
    0,
  );
  const totalQty = cartItems.reduce(
    (sum, item) => sum + (item.quantity || 1),
    0,
  );
  const discountedGems =
    discountPercent > 0
      ? Math.round(totalGems * (1 - discountPercent / 100))
      : totalGems;

  // Rendered in the sticky sidebar on desktop and in the sticky bar on mobile,
  // so the terms gate always sits next to the checkout button it blocks.
  const termsCheckbox = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "16px",
      }}
    >
      <input
        type="checkbox"
        id="agreeTerms"
        checked={agreeToTerms}
        onChange={(e) => setAgreeToTerms(e.target.checked)}
        style={{
          accentColor: GOLD,
          width: "16px",
          height: "16px",
          cursor: "pointer",
        }}
      />
      <label
        htmlFor="agreeTerms"
        style={{
          color: GOLD_DIM,
          fontSize: "12px",
          cursor: "pointer",
          display: "flex",
          gap: 6,
          alignItems: "center",
        }}
      >
        <span style={{ cursor: "pointer" }}>{t("cart.agreeToThe")}</span>
        <a
          href="/terms-conditions"
          onClick={(e) => {
            e.preventDefault();
            navigate("/terms-conditions", { state: { isModal: true } });
          }}
          style={{
            color: "rgba(223,189,105,0.9)",
            textDecoration: "underline",
            marginLeft: 2,
          }}
        >
          {t("cart.termsAndConditions")}
        </a>
      </label>
    </div>
  );

  // Rendered once and placed either in the sticky desktop sidebar or below the
  // item list on mobile, where the checkout action moves into the sticky bar.
  const orderSummary = (
    <div style={styles.panel}>
      <div style={{ ...styles.panelHeader, marginBottom: "16px" }}>
        <h3
          style={{
            color: GOLD,
            margin: 0,
            fontSize: "16px",
            fontWeight: 700,
          }}
        >
          {t("cart.orderSummary")}
        </h3>
      </div>

      {/* Discount Voucher */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "18px" }}>
        <input
          type="text"
          placeholder={t("cart.discountVoucher")}
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "8px 12px",
            borderRadius: "28px",
            border: "1px solid rgba(223,189,105,0.3)",
            background: "rgba(255,255,255,0.06)",
            color: GOLD,
            fontSize: "13px",
            outline: "none",
          }}
        />
        <button
          onClick={handleApplyDiscount}
          style={{
            flexShrink: 0,
            padding: "8px 14px",
            backgroundColor: GOLD,
            color: "#151517",
            border: "none",
            borderRadius: "28px",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: "13px",
            whiteSpace: "nowrap",
          }}
        >
          {t("cart.apply")}
        </button>
      </div>

      {/* Summary Lines */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          marginBottom: "18px",
        }}
      >
        <div style={styles.summaryRow}>
          <span>
            {t("cart.subtotal")} ({totalQty} {t("checkout.items")})
          </span>
          <span style={{ whiteSpace: "nowrap" }}>
            {format(totalCard)} / {totalGems} {t("cart.gems")}
          </span>
        </div>
        {discountPercent > 0 && (
          <div style={styles.summaryRow}>
            <span>
              {t("cart.discount")}({discountPercent}%)
            </span>
            <span style={{ color: "#6ee7b7" }}>
              -{totalGems - discountedGems} {t("cart.gems")}
            </span>
          </div>
        )}
        <div
          style={{
            height: "1px",
            background: "rgba(223,189,105,0.2)",
            margin: "2px 0",
          }}
        />
        <div
          style={{
            ...styles.summaryRow,
            color: GOLD,
            fontWeight: "bold",
            fontSize: "18px",
          }}
        >
          <span>{t("cart.total")}</span>
          <span style={{ whiteSpace: "nowrap" }}>
            {format(totalCard)} / {discountedGems}{" "}
            {t("cart.gems")}
          </span>
        </div>
      </div>

      {/* Terms checkbox — on mobile it sits in the sticky bar instead, right
          next to the button it gates. */}
      {isDesktop && termsCheckbox}

      {/* Desktop: the checkout action sits in-flow at the bottom of the sticky
          sidebar. On mobile it lives in the sticky bar instead. */}
      {isDesktop && (
        <button
          onClick={handleBuyNow}
          disabled={!agreeToTerms}
          style={{
            ...styles.checkoutBtn,
            cursor: agreeToTerms ? "pointer" : "not-allowed",
            opacity: agreeToTerms ? 1 : 0.6,
          }}
        >
          {t("cart.checkoutNow")}
        </button>
      )}
    </div>
  );

  return (
    <>
      <div style={styles.container}>
        <div
          style={{
            ...styles.wrapper,
            maxWidth: isDesktop ? "1200px" : "760px",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "24px",
              gap: "8px",
            }}
          >
            <button
              onClick={handleBack}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: GOLD,
                fontSize: "20px",
                padding: "0 8px 0 0",
                lineHeight: 1,
              }}
            >
              ←
            </button>
            <h2
              style={{
                ...styles.heading,
                marginBottom: 0,
                fontFamily: "DMSans",
              }}
            >
              {t("cart.yourCart")}
            </h2>
          </div>

          {cartItems.length === 0 ? (
            <p style={styles.emptyText}>{t("cart.emptyCart")}</p>
          ) : (
            <div style={isDesktop ? styles.desktopGrid : undefined}>
              {/* LEFT: item list */}
              <div style={isDesktop ? styles.leftColumn : undefined}>
                <div style={styles.panel}>
                  <div style={styles.panelHeader}>
                    <span style={{ ...styles.itemName, fontSize: "14px" }}>
                      {t("cart.yourCart")} ({totalQty})
                    </span>
                  </div>

                  {cartItems.map((item, index) => (
                    <div
                      key={item.Uid || index}
                      style={{
                        display: "flex",
                        gap: "12px",
                        padding: "16px 0",
                        borderBottom:
                          index < cartItems.length - 1
                            ? "1px solid rgba(255,255,255,0.06)"
                            : "none",
                      }}
                    >
                      {item.IMAGE ? (
                        <img
                          src={item.IMAGE}
                          alt={item.Name}
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: GOLD_DIM,
                            fontSize: "11px",
                            flexShrink: 0,
                          }}
                        >
                          {t("cart.noImage")}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={styles.itemName}>
                          {item.Name || item.DESC_OTH || "Unnamed Product"}
                        </p>
                        {item.Remark && (
                          <p style={styles.itemMeta}>{item.Remark}</p>
                        )}

                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: "12px",
                            flexWrap: "wrap",
                            marginTop: "12px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                color: GOLD,
                                fontWeight: 700,
                                fontSize: "15px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.GemAmount || 0} {t("cart.gems")}
                            </div>
                            <div
                              style={{
                                color: GOLD_DIM,
                                fontSize: "12px",
                                marginTop: 2,
                              }}
                            >
                              {format(convert(item.CardAmount || 0))}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div style={styles.qtyStepper}>
                              <button
                                onClick={() => decreaseQty(index)}
                                style={styles.qtyStepBtn}
                              >
                                −
                              </button>
                              <span
                                style={{
                                  color: GOLD,
                                  fontWeight: 600,
                                  minWidth: "20px",
                                  textAlign: "center",
                                }}
                              >
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => increaseQty(index)}
                                style={styles.qtyStepBtn}
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(index)}
                              title={t("cart.remove")}
                              style={styles.iconBtn}
                              aria-label={t("cart.remove")}
                            >
                              <FaRegTrashAlt />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT: order summary — sticky sidebar on desktop, stacked
                  below the items on mobile */}
              <div
                style={isDesktop ? styles.rightColumn : { marginTop: "16px" }}
              >
                {orderSummary}
              </div>
            </div>
          )}
        </div>

        {/* Sticky checkout bar — mobile only; on desktop the same action lives
            in the order summary card above. */}
        {!isDesktop && cartItems.length > 0 && (
          <div style={styles.footerBar}>
            <div style={styles.footerInner}>
              {/* Full-width own row above the total; the negative margin
                  offsets the checkbox's own spacing from the sidebar layout. */}
              <div style={{ flexBasis: "100%", marginBottom: "-8px" }}>
                {termsCheckbox}
              </div>
              <div>
                <span style={{ color: GOLD_DIM, fontSize: "13px" }}>
                  {t("cart.total")}:{" "}
                </span>
                <span
                  style={{ color: GOLD, fontSize: "18px", fontWeight: 800 }}
                >
                  {format(totalCard)} / {discountedGems}{" "}
                  {t("cart.gems")}
                </span>
              </div>
              <button
                onClick={handleBuyNow}
                disabled={!agreeToTerms}
                style={{
                  ...styles.checkoutBtn,
                  width: "auto",
                  padding: "13px 28px",
                  minWidth: "160px",
                  cursor: agreeToTerms ? "pointer" : "not-allowed",
                  opacity: agreeToTerms ? 1 : 0.6,
                }}
              >
                {t("cart.checkoutNow")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Guardian Confirmation Modal (for users under 18 attempting checkout) */}
      {guardianModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
          }}
          onClick={() => setGuardianModal(false)}
        >
          <div
            style={{
              backgroundColor: "#1a1a1a",
              borderRadius: 12,
              padding: "28px 24px",
              width: "min(460px, 92vw)",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
              border: "1px solid rgba(223,189,105,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                color: "#DFBD69",
                margin: "0 0 16px",
                fontSize: "1.2rem",
              }}
            >
              {t("cart.guardianConfirmationRequired")}
            </h3>
            <p style={{ color: "#fff2d2", marginBottom: 28, lineHeight: 1.6 }}>
              {t("cart.guardianMessage")}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                style={{
                  background: "#DFBD69",
                  color: "#151517",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 22px",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: "0.95rem",
                }}
                onClick={() => {
                  setGuardianModal(false);
                  proceedBuyNow();
                }}
              >
                {t("cart.yesProceed")}
              </button>
              <button
                style={{
                  background: "transparent",
                  color: "#DFBD69",
                  border: "1px solid rgba(223,189,105,0.4)",
                  borderRadius: 8,
                  padding: "10px 22px",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                }}
                onClick={() => setGuardianModal(false)}
              >
                {t("cart.noCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Cart;
